import { RefObject } from "react"

export const getSpacerHeightRelativeToNewUserMessage = (
    scrollContainerRef: RefObject<HTMLDivElement>,
    messageElement: HTMLDivElement | null,
    loadingIndicatorRef: RefObject<HTMLDivElement>,
    messagesContainerRef: RefObject<HTMLDivElement>,
): number => {
    if (
        !scrollContainerRef.current ||
        !messageElement ||
        !loadingIndicatorRef.current
    ) {
        return 0
    }

    const container = scrollContainerRef.current

    const messageElementClientRect = messageElement.getBoundingClientRect()
    const loadingIndicatorClientRect =
        loadingIndicatorRef.current.getBoundingClientRect()

    const messageHeight = messageElementClientRect.height

    // don't forget to add the height of the loading element
    const loadingIndicatorHeight = loadingIndicatorClientRect.height

    // include the space between elements,
    // that are introduced because of the gap used in the flex container
    // the gaps appear:
    // - above and below message element
    // - above and below loading indicator
    // - above spacer (that's the last element, there is nothing below it)
    const gap = getGap(messagesContainerRef)

    const viewPortHeight = container.offsetHeight

    // the correct spacer height is:
    // full view port height + message height + loading indicator height + gaps
    // this is regardless of where the original scroll location was
    const spacerHeight =
        viewPortHeight - (messageHeight + loadingIndicatorHeight + gap * 5)

    // ensure we don't try to set negative height
    return spacerHeight > 0 ? spacerHeight : 0
}

export const getSpacerHeightRelativeToLastMessage = (
    scrollContainerRef: RefObject<HTMLDivElement>,
    messageElement: HTMLDivElement | null,
    messagesContainerRef: RefObject<HTMLDivElement>,
): number => {
    if (!scrollContainerRef.current || !messageElement) {
        return 0
    }
    const container = scrollContainerRef.current

    const messageElementBottom =
        messageElement.offsetTop + messageElement.clientHeight
    const viewportBottom = container.scrollTop + container.clientHeight

    // gap appears below last the message and above spacer
    const gap = getGap(messagesContainerRef)
    const offset = viewportBottom - messageElementBottom - 2 * gap

    // if the messages overflow, we remove the spacer
    return offset > 0 ? offset : 0
}

// to calculate the dynamic distances between elements
// to not rely on fixed numbers, we read it from the css
// because the gap class could change any moment
// defaulting with 0 in case the class is removed
const getGap = (container: RefObject<HTMLDivElement>): number => {
    if (!container.current) {
        return 0
    }

    return parseFloat(getComputedStyle(container.current).gap || "0")
}

// for scrolling the message to the top of the modal
export const scrollElement = (element: HTMLDivElement): void => {
    element.scrollIntoView({
        block: "start",
        behavior: "smooth",
    })
}

// because we don't use the sticky footer anymore
// we need to ensure we know when to show/hide the shadow
// for this we need to know if the element above has been
// scrolled fully to the bottom or not
export const checkIfContainerIsScrolledToBottom = (
    container: RefObject<HTMLDivElement>,
): boolean => {
    const element = container.current
    if (!element) {
        return true
    }

    return element.scrollHeight - element.scrollTop <= element.clientHeight + 1
}
